import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  App,
  Space,
  Typography,
  Spin,
  Divider,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getClientPatientById, updateClientPatient, updateClientStatus } from '../../services/clients';
import { lookupPostalCodeAddress } from '../../services/geo';
import { GENDER_OPTIONS, BLOOD_TYPE_OPTIONS } from '../../utils/constants';

const { useApp } = App;
const { Title, Text } = Typography;

const ClientUpdatePatientPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [client, setClient] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [postalCodeData, setPostalCodeData] = useState(null);
  const [loadingPostalCode, setLoadingPostalCode] = useState(false);

  // Cargar datos del paciente
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    getClientPatientById(id)
      .then((response) => {
        const data = response.data || response;
        setClient(data);
        setIsActive(data.isActive);

        const { patientProfile, address } = data;

        // Guardar el postalCodeId existente si hay address
        const existingPostalCodeId = address?.postalCode?.id;

        // Establecer valores del formulario
        form.setFieldsValue({
          firstName: data.firstName,
          lastName: data.lastName,
          secondLastName: data.secondLastName || undefined,
          email: data.email,
          phone: data.phone?.replace(/^\+52/, '') || undefined,
          profile: 'Paciente',
          acceptsPrivacy: data.acceptsPrivacy ?? true,
          birthDate: patientProfile?.birthDate ? dayjs(patientProfile.birthDate) : undefined,
          gender: patientProfile?.gender || undefined,
          bloodType: patientProfile?.bloodType || undefined,
          weight: patientProfile?.weight || undefined,
          height: patientProfile?.height || undefined,
          bloodPressure: patientProfile?.bloodPressure || undefined,
          oxygenation: patientProfile?.oxygenation || undefined,
          heartRate: patientProfile?.heartRate || undefined,
          postalCode: address?.postalCode?.cp || undefined,
          cityState: address?.postalCode
            ? `${address.postalCode.ciudad || address.postalCode.municipio}, ${address.postalCode.estado}`
            : undefined,
          street: address?.street || undefined,
          details: address?.details || undefined,
          postalCodeId: existingPostalCodeId,
        });

        // Si hay código postal pero no postalCodeId, cargar datos para obtenerlo
        if (address?.postalCode?.cp && !existingPostalCodeId) {
          loadPostalCodeData(address.postalCode.cp);
        }
      })
      .catch((error) => {
        console.error(error);
        const errorMessage = error?.__normalized?.message ?? 'No se pudo cargar el paciente.';
        message.error(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, form, message]);

  // Función para cargar datos del código postal
  const loadPostalCodeData = useCallback(async (cp) => {
    if (!cp || cp.length !== 5) {
      setPostalCodeData(null);
      form.setFieldsValue({ cityState: undefined, postalCodeId: undefined });
      return;
    }

    setLoadingPostalCode(true);
    try {
      const response = await lookupPostalCodeAddress(cp);
      const data = response.data || response;
      setPostalCodeData(data);
      
      // Si hay datos, establecer ciudad/estado
      // La respuesta es un array, tomar el primer elemento
      if (data && Array.isArray(data) && data.length > 0) {
        const firstState = data[0]; // Primer estado
        const firstMunicipio = firstState.municipios?.[0]; // Primer municipio
        if (firstMunicipio) {
          const cityState = `${firstMunicipio.ciudad || firstMunicipio.municipio}, ${firstState.estado}`;
          form.setFieldsValue({ 
            cityState,
            postalCodeId: firstMunicipio.idMunicipio,
          });
        }
      } else {
        form.setFieldsValue({ cityState: undefined, postalCodeId: undefined });
      }
    } catch (error) {
      console.error('Error al buscar código postal:', error);
      setPostalCodeData(null);
      form.setFieldsValue({ cityState: undefined, postalCodeId: undefined });
      message.warning('No se encontró información para este código postal');
    } finally {
      setLoadingPostalCode(false);
    }
  }, [form, message]);

  // Manejar cambio de código postal
  const handlePostalCodeChange = (e) => {
    const cp = e.target.value.replace(/\D/g, '').slice(0, 5);
    form.setFieldsValue({ postalCode: cp });
    
    if (cp.length === 5) {
      loadPostalCodeData(cp);
    } else {
      setPostalCodeData(null);
      form.setFieldsValue({ cityState: undefined, postalCodeId: undefined });
    }
  };

  // Manejar cambio de estado activo
  const handleStatusChange = async (checked) => {
    try {
      await updateClientStatus(id, checked);
      setIsActive(checked);
      message.success(`Cliente ${checked ? 'activado' : 'desactivado'} correctamente.`);
    } catch (error) {
      console.error(error);
      const errorMessage = error?.__normalized?.message ?? 'No se pudo actualizar el estado.';
      message.error(errorMessage);
      setIsActive(!checked); // Revertir cambio
    }
  };

  // Enviar formulario
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // Obtener postalCodeId del formulario o de los datos cargados
      let postalCodeId = values.postalCodeId;
      if (!postalCodeId && postalCodeData && Array.isArray(postalCodeData) && postalCodeData.length > 0) {
        const firstState = postalCodeData[0];
        const firstMunicipio = firstState.municipios?.[0];
        if (firstMunicipio?.idMunicipio) {
          postalCodeId = firstMunicipio.idMunicipio;
        }
      }

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone ? (values.phone.startsWith('+52') ? values.phone : `+52${values.phone}`) : undefined,
        acceptsPrivacy: values.acceptsPrivacy ?? true,
        birthDate: values.birthDate ? values.birthDate.format('YYYY-MM-DD') : undefined,
        gender: values.gender,
        bloodType: values.bloodType,
        weight: values.weight,
        height: values.height,
        bloodPressure: values.bloodPressure,
        oxygenation: values.oxygenation,
        heartRate: values.heartRate,
        address: {
          postalCodeId: postalCodeId,
          street: values.street,
          details: values.details,
        },
      };

      await updateClientPatient(id, payload);
      message.success('Paciente actualizado correctamente.');
      navigate('/clients/all');
    } catch (error) {
      console.error(error);
      const errorMessage = error?.__normalized?.message ?? 'No se pudo actualizar el paciente.';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/clients/all');
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ padding: 0, height: 'auto' }}
            >
              <Title level={3} style={{ margin: 0 }}>
                Editar Cliente
              </Title>
            </Button>
          </div>
          <Switch
            checked={isActive}
            onChange={handleStatusChange}
            checkedChildren="Activo"
            unCheckedChildren="Inactivo"
            style={{ backgroundColor: isActive ? '#52c41a' : undefined }}
          />
        </div>

        {/* Formulario */}
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            initialValues={{
              acceptsPrivacy: true,
              profile: 'Paciente',
            }}
          >
            {/* Información Personal */}
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Nombre"
                  name="firstName"
                  rules={[{ required: true, message: 'Nombre requerido' }]}
                >
                  <Input placeholder="Nombre" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Apellidos"
                  name="lastName"
                  rules={[{ required: true, message: 'Apellidos requeridos' }]}
                >
                  <Input placeholder="Apellidos" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Correo electrónico"
                  name="email"
                  rules={[
                    { required: true, message: 'El correo es requerido' },
                    { type: 'email', message: 'Correo electrónico inválido' },
                  ]}
                >
                  <Input placeholder="correo@ejemplo.com" disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Teléfono móvil"
                  name="phone"
                  rules={[
                    { pattern: /^\d{10}$/, message: 'Debe tener 10 dígitos' },
                  ]}
                >
                  <Input
                    placeholder="00000000000"
                    maxLength={10}
                    onChange={(e) => {
                      const onlyDigits = e.target.value.replace(/\D/g, '');
                      form.setFieldsValue({ phone: onlyDigits });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Perfil" name="profile">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Código Postal"
                  name="postalCode"
                  rules={[
                    { required: true, message: 'El código postal es requerido' },
                    { pattern: /^\d{5}$/, message: 'Debe tener 5 dígitos' },
                  ]}
                >
                  <Input
                    placeholder="00000"
                    maxLength={5}
                    onChange={handlePostalCodeChange}
                    suffix={loadingPostalCode ? <Spin size="small" /> : null}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Ciudad/Estado" name="cityState">
                  <Input placeholder="Ciudad, Estado" disabled />
                </Form.Item>
              </Col>
            </Row>

{/*
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Calle" name="street">
                  <Input placeholder="Calle y número" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Detalles adicionales" name="details">
                  <Input placeholder="Colonia, referencia, etc." />
                </Form.Item>
              </Col>
            </Row>
*/}
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Fecha de nacimiento"
                  name="birthDate"
                  rules={[{ required: true, message: 'La fecha de nacimiento es requerida' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Género"
                  name="gender"
                  rules={[{ required: true, message: 'El género es requerido' }]}
                >
                  <Select placeholder="Selecciona un género" options={GENDER_OPTIONS} />
                </Form.Item>
              </Col>

            </Row>

            <Divider />
            {/* Información Médica */}
            <Row gutter={16}>
            <Col xs={24} md={8}>
                <Form.Item
                  label="Tipo de Sangre"
                  name="bloodType"
                  rules={[{ required: true, message: 'El tipo de sangre es requerido' }]}
                >
                  <Select placeholder="Selecciona tipo de sangre" options={BLOOD_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Peso (kg)" name="weight">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="70"
                    min={0}
                    max={500}
                    precision={1}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Altura (m)" name="height">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="1.75"
                    min={0}
                    max={3}
                    precision={2}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Presión Arterial" name="bloodPressure">
                  <Input placeholder="120/80" maxLength={7} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Oxigenación (%)" name="oxygenation">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="98"
                    min={0}
                    max={100}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Frecuencia Cardiaca (bpm)" name="heartRate">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="72"
                    min={0}
                    max={300}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Campo oculto para postalCodeId */}
            <Form.Item name="postalCodeId" hidden>
              <Input />
            </Form.Item>

            {/* Botones */}
            <Row>
              <Col>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Aceptar
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

export default ClientUpdatePatientPage;

